import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Maximize2, X, Loader2, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { ChartInfoButton } from "@/components/ChartInfoButton";
import type { ChartContextSource } from "@/analytics/charts/chart.context";

export interface ChartItem extends ChartContextSource {
  isLoading?: boolean;
  hasError?: boolean;
  onRetry?: () => void;
  render: (heightClass: string) => React.ReactNode;
}

interface SimpleChartGridProps {
  items: ChartItem[];
  firstCardWidthRef?: (w: number) => void;
}

// Scale factor and dimensions for fullscreen "Up next" preview thumbnail
const PREVIEW_SCALE = 0.35;
const PREVIEW_CHART_W = 500;
const PREVIEW_CHART_H = 250;
const PREVIEW_W = Math.round(PREVIEW_CHART_W * PREVIEW_SCALE);
const PREVIEW_H = Math.round(PREVIEW_CHART_H * PREVIEW_SCALE);

const SimpleChartGrid = ({ items, firstCardWidthRef }: SimpleChartGridProps) => {
  const [fullscreenIdx, setFullscreenIdx] = useState<number | null>(null);
  const firstRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!firstCardWidthRef || !firstRef.current) return;
    const obs = new ResizeObserver(entries => firstCardWidthRef(entries[0].contentRect.width));
    obs.observe(firstRef.current);
    return () => obs.disconnect();
  }, [firstCardWidthRef]);

  // Keyboard navigation for fullscreen
  useEffect(() => {
    if (fullscreenIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFullscreenIdx(null);
      if (e.key === "ArrowLeft") setFullscreenIdx(i => (i !== null && i > 0 ? i - 1 : i));
      if (e.key === "ArrowRight") setFullscreenIdx(i => (i !== null && i < items.length - 1 ? i + 1 : i));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [fullscreenIdx, items.length]);

  if (!items.length) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-96">
          <p className="text-muted-foreground">No charts available</p>
        </CardContent>
      </Card>
    );
  }

  // Clamp fullscreen index so removing items mid-view doesn't crash
  const clampedIdx = fullscreenIdx !== null
    ? Math.max(0, Math.min(fullscreenIdx, items.length - 1))
    : null;
  const fs = clampedIdx !== null ? items[clampedIdx] : null;
  const canGoPrev = clampedIdx !== null && clampedIdx > 0;
  const canGoNext = clampedIdx !== null && clampedIdx < items.length - 1;
  const nextItem = canGoNext && clampedIdx !== null ? items[clampedIdx + 1] : null;

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
        {items.map((it, idx) => {
          const isOddLast = idx === items.length - 1 && items.length % 2 !== 0;
          return (
            <div
              key={it.id}
              ref={idx === 0 ? firstRef : undefined}
              className={isOddLast ? "lg:col-start-2 lg:col-span-2" : "lg:col-span-2"}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0" style={{ padding: "5px" }}>
                  <div className="flex items-center gap-1 min-w-0">
                    <CardTitle className="!text-sm truncate">{it.title}</CardTitle>
                    <ChartInfoButton title={it.title} description={it.description} />
                  </div>
                </CardHeader>
                <CardContent style={{ padding: "5px" }}>
                  <div className="relative w-full">
                    {it.render("h-[250px]")}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setFullscreenIdx(idx)}
                      aria-label="Fullscreen"
                      className="absolute bottom-1 left-1 h-6 w-6 bg-background/70 hover:bg-primary/10 transition-all duration-200 hover:scale-110 shadow-sm lg:left-auto lg:right-1"
                    >
                      <Maximize2 className="h-3.5 w-3.5" />
                    </Button>
                    {it.hasError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background/75 rounded">
                        <p className="text-sm text-muted-foreground">Failed to load</p>
                        {it.onRetry && (
                          <Button size="sm" variant="outline" onClick={it.onRetry}>
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                            Retry
                          </Button>
                        )}
                      </div>
                    )}
                    {it.isLoading && !it.hasError && (
                      <div className="absolute top-2 right-2 pointer-events-none">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {fs && clampedIdx !== null && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-md"
            onClick={() => setFullscreenIdx(null)}
          />

          <div className="fixed inset-0 z-50 flex flex-col overflow-hidden bg-gradient-to-b from-background via-background to-muted/30">

            {/* Header */}
            <div className="pt-safe shrink-0 flex items-center justify-between gap-2 px-3 pt-4 pb-3 sm:px-10 sm:pt-6 sm:pb-4 lg:px-20">
              <div className="flex items-center gap-2 min-w-0 sm:gap-3">
                <h2 className="truncate text-base font-semibold tracking-tight sm:text-2xl">{fs.title}</h2>
                <ChartInfoButton title={fs.title} description={fs.description} />
                <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground shrink-0">
                  {clampedIdx + 1} / {items.length}
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setFullscreenIdx(null)}
                aria-label="Exit fullscreen"
                className="h-9 w-9 rounded-full hover:bg-destructive/10 hover:text-destructive transition-all duration-200 shrink-0"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="shrink-0 h-px mx-6 bg-gradient-to-r from-transparent via-border to-transparent" />

            {/* Chart area + side nav arrows */}
            <div className="flex-1 min-h-0 relative flex items-center px-2 py-3 sm:px-12 sm:py-4 lg:px-20">
              <button
                disabled={!canGoPrev}
                onClick={() => setFullscreenIdx(i => (i !== null ? i - 1 : i))}
                aria-label="Previous chart"
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 sm:left-3
                  h-9 w-9 sm:h-12 sm:w-12 rounded-full flex items-center justify-center
                  bg-background/80 backdrop-blur-sm border border-border/60
                  shadow-lg hover:bg-primary/10 hover:border-primary/40 hover:scale-110
                  transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-background/80"
              >
                <ChevronLeft className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>

              <div className="w-full h-full rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden flex flex-col">
                <div className="shrink-0 flex items-center gap-2 px-5 pt-4 pb-2 border-b border-border/40">
                  <div className="h-2 w-2 rounded-full bg-primary/60" />
                  <span className="text-sm font-medium text-muted-foreground">{fs.title}</span>
                </div>
                <div className="flex-1 min-h-0 p-4">
                  {fs.render("h-full")}
                </div>
              </div>

              <button
                disabled={!canGoNext}
                onClick={() => setFullscreenIdx(i => (i !== null ? i + 1 : i))}
                aria-label="Next chart"
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 sm:right-3
                  h-9 w-9 sm:h-12 sm:w-12 rounded-full flex items-center justify-center
                  bg-background/80 backdrop-blur-sm border border-border/60
                  shadow-lg hover:bg-primary/10 hover:border-primary/40 hover:scale-110
                  transition-all duration-200 disabled:opacity-20 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-background/80"
              >
                <ChevronRight className="h-5 w-5 sm:h-6 sm:w-6" />
              </button>
            </div>

            {/* Bottom strip — dots + "Up next" preview */}
            <div className="pb-safe shrink-0 px-3 pb-4 pt-2 sm:px-6 sm:pb-5 sm:pt-3">
              <div className="h-px mb-4 bg-gradient-to-r from-transparent via-border to-transparent" />

              <div className="flex items-center justify-center gap-4 sm:gap-10">
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {items.map((c, i) => (
                    <button
                      key={c.id}
                      onClick={() => setFullscreenIdx(i)}
                      aria-label={`Go to ${c.title}`}
                      className={`rounded-full transition-all duration-300 ${
                        i === clampedIdx
                          ? "w-5 h-2 bg-primary"
                          : "w-2 h-2 bg-muted-foreground/25 hover:bg-muted-foreground/50"
                      }`}
                    />
                  ))}
                </div>

                {nextItem && (
                  <button
                    className="group hidden flex-col items-center gap-1.5 rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm px-3 pt-2 pb-2.5 shadow-sm
                      hover:border-primary/40 hover:bg-card hover:shadow-md transition-all duration-200 sm:flex"
                    onClick={() => setFullscreenIdx(i => (i !== null ? i + 1 : i))}
                    aria-label={`Go to ${nextItem.title}`}
                  >
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium self-start flex items-center gap-1">
                      Up next <ChevronRight className="h-3 w-3" />
                    </span>
                    <div
                      className="rounded-lg border border-border/40 bg-background overflow-hidden shrink-0"
                      style={{ width: PREVIEW_W, height: PREVIEW_H, position: "relative" }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: PREVIEW_CHART_W,
                          height: PREVIEW_CHART_H,
                          transform: `scale(${PREVIEW_SCALE})`,
                          transformOrigin: "top left",
                          pointerEvents: "none",
                        }}
                      >
                        {nextItem.render("h-[250px]")}
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-foreground text-center w-full group-hover:text-primary transition-colors">
                      {nextItem.title}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SimpleChartGrid;
